import { useState, useEffect } from "react";
import axios from "axios";
import proxy from "../proxy";
import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  Skeleton,
} from "@chakra-ui/react";

const WeeklyCoach = () => {
  const [coach, setCoach] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${proxy}server_mts/GetWeeklyCoach.php`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .then((res) => {
        if (res.data?.insight_text) {
          setCoach(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <Box
        maxW="700px"
        mx="auto"
        p={{ base: 3, md: 4 }}
        bg="rgba(45, 55, 72, 0.5)"
        borderRadius="xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
      >
        <Skeleton height="14px" mb={2} startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
        <Skeleton height="14px" mb={2} startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
        <Skeleton height="14px" width="60%" startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
      </Box>
    );
  }

  if (!coach) return null;

  const date = new Date(coach.created_at);
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  // How many days ago was this posted
  const daysAgo = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
  const timeLabel = daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday" : `${daysAgo}d ago`;

  return (
    <Box maxW="700px" mx="auto" position="relative" overflow="hidden">
      <Box
        p={{ base: 3, md: 4 }}
        bg="linear-gradient(135deg, rgba(72, 187, 120, 0.08), rgba(56, 178, 172, 0.08))"
        borderRadius="xl"
        border="1px solid"
        borderColor="rgba(72, 187, 120, 0.2)"
        backdropFilter="blur(10px)"
        transition="all 0.3s"
        _hover={{
          borderColor: "rgba(72, 187, 120, 0.4)",
          boxShadow: "0 8px 30px rgba(72, 187, 120, 0.15)",
        }}
      >
        {/* Green gradient top accent */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="2px"
          bgGradient="linear(to-r, green.400, teal.400)"
          opacity={0.6}
        />

        <VStack spacing={{ base: 2, md: 2.5 }} align="stretch">
          {/* Header */}
          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <HStack spacing={2}>
              <Badge
                bg="linear-gradient(135deg, #48BB78, #38A169)"
                color="white"
                fontSize={{ base: "2xs", md: "xs" }}
                px={2}
                py={0.5}
                borderRadius="full"
                fontWeight="bold"
              >
                🏋️ Weekly Coach
              </Badge>
              <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                {formattedDate} • {timeLabel}
              </Text>
            </HStack>

            <HStack spacing={2}>
              <Badge
                colorScheme="green"
                fontSize={{ base: "2xs", md: "xs" }}
                px={2}
                py={0.5}
                borderRadius="full"
                variant="subtle"
              >
                {coach.milestones_reached} milestones
              </Badge>
              <Badge
                colorScheme="teal"
                fontSize={{ base: "2xs", md: "xs" }}
                px={2}
                py={0.5}
                borderRadius="full"
                variant="subtle"
              >
                ${Number(coach.monthly_dividends).toFixed(2)}/mo
              </Badge>
            </HStack>
          </HStack>

          {/* Coach message */}
          <Text
            fontSize={{ base: "sm", md: "md" }}
            color="gray.200"
            lineHeight="tall"
            fontWeight="medium"
          >
            {coach.insight_text}
          </Text>

          {/* Footer */}
          <HStack spacing={1} justify="flex-end">
            <Text fontSize="2xs" color="gray.600" fontStyle="italic">
              Powered by Gemini AI • Updates weekly
            </Text>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default WeeklyCoach;